/**
 * SuperDoc v2's table resize overlay zips unique `.superdoc-table-fragment`
 * nodes (DOM order, including headers/footers) against `tables.list()`
 * (story order, body first). A footer table that appears first in the DOM
 * shifts every body index, so dragging table N writes table N+1.
 *
 * SuperDoc's overlay is pointer-disabled. We handle the drag and call
 * `tables.setColumnWidth` with the table id for the fragment that was grabbed.
 */

export interface SuperDocListedTable {
  tableNodeId: string;
  [key: string]: unknown;
}

export interface SuperDocListedTableStory {
  storyId?: string;
  tables?: SuperDocListedTable[];
  [key: string]: unknown;
}

export interface SuperDocTableListResult {
  stories?: SuperDocListedTableStory[];
  [key: string]: unknown;
}

export interface SuperDocTableFragmentRef {
  blockId: string;
  story: string;
}

function normalizeStoryKey(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
}

function relationshipId(value: string): string | null {
  const match = value.match(/rid\d+/i);
  return match ? match[0].toLowerCase() : null;
}

function storyIdentityKey(value: string): string {
  const kind = storyKind(value);
  const rid = relationshipId(value);
  if (rid) return `${kind}:${rid}`;
  return `${kind}:${normalizeStoryKey(value)}`;
}

function uniqueStoriesOfKind(stories: string[], kind: 'header' | 'footer'): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const story of stories) {
    if (storyKind(story) !== kind) continue;
    const key = storyIdentityKey(story);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(story);
  }
  return unique;
}

function storyIdFromUnknown(value: unknown): string {
  if (typeof value === 'string' && value) return value;
  if (!value || typeof value !== 'object') return '';
  const record = value as {
    headerFooterKind?: unknown;
    refId?: unknown;
    storyId?: unknown;
    storyType?: unknown;
  };
  if (typeof record.storyId === 'string' && record.storyId) return record.storyId;
  if (typeof record.storyType === 'string') {
    if (record.storyType === 'headerFooterPart' && typeof record.refId === 'string') {
      return `${record.storyType}:${record.refId}`;
    }
    if (typeof record.headerFooterKind === 'string') return record.headerFooterKind;
    return record.storyType;
  }
  return '';
}

export function storyKind(value: string): 'body' | 'header' | 'footer' | 'other' {
  const lower = value.trim().toLowerCase();
  if (
    lower === 'body'
    || lower.startsWith('body')
    || lower === 'main'
    || lower.startsWith('main:')
  ) {
    return 'body';
  }
  if (lower.startsWith('footer') || lower.includes('/footer') || /:footer(?:part)?(?::|$)/.test(lower)) {
    return 'footer';
  }
  if (lower.startsWith('header') && !lower.startsWith('headerfooter')) {
    return 'header';
  }
  if (lower.includes('/header') || /:header(?:part)?(?::|$)/.test(lower)) return 'header';
  return 'other';
}

function isBodyStory(value: string): boolean {
  return storyKind(value) === 'body';
}

function blockIdsMatch(left: string, right: string): boolean {
  if (left === right) return true;
  return layoutTableKey(left) === layoutTableKey(right);
}

export function layoutTableKey(blockId: string): string {
  return blockId.replace(/\/o\d+$/i, '');
}

export function layoutTableOrdinal(blockId: string): number | null {
  const match = layoutTableKey(blockId).match(/\/table\/(\d+)/i);
  return match ? Number(match[1]) : null;
}

function storiesAlign(layoutStory: string, candidateStory: string): boolean {
  if (normalizeStoryKey(layoutStory) === normalizeStoryKey(candidateStory)) return true;
  if (storyIdentityKey(layoutStory) === storyIdentityKey(candidateStory)) return true;
  const layoutRid = relationshipId(layoutStory);
  const candidateRid = relationshipId(candidateStory);
  if (layoutRid || candidateRid) return layoutRid === candidateRid;
  const kind = storyKind(layoutStory);
  if (kind === 'other' || storyKind(candidateStory) !== kind) return false;
  return true;
}

export function flattenListedTableNodeIds(listed: SuperDocTableListResult | null | undefined): string[] {
  const ids: string[] = [];
  for (const story of listed?.stories ?? []) {
    for (const table of story.tables ?? []) {
      const tableNodeId = typeof table.tableNodeId === 'string' && table.tableNodeId
        ? table.tableNodeId
        : typeof table.nodeId === 'string' ? table.nodeId : '';
      if (tableNodeId) ids.push(tableNodeId);
    }
  }
  return ids;
}

function storyQueues(listed: SuperDocTableListResult): Array<{
  storyId: string;
  tables: SuperDocListedTable[];
}> {
  return (listed.stories ?? []).map((story) => ({
    storyId: storyIdFromUnknown(story.storyId),
    tables: (story.tables ?? []).map((table) => {
      const tableNodeId = typeof table.tableNodeId === 'string' && table.tableNodeId
        ? table.tableNodeId
        : typeof table.nodeId === 'string' ? table.nodeId : '';
      return { ...table, tableNodeId };
    }).filter((table) => table.tableNodeId),
  }));
}

function matchingQueueForLayoutStory(
  layoutStory: string,
  fragments: SuperDocTableFragmentRef[],
  queues: Array<{ storyId: string; tables: SuperDocListedTable[] }>,
): { storyId: string; tables: SuperDocListedTable[] } | null {
  const layoutKey = normalizeStoryKey(layoutStory);
  const layoutRid = relationshipId(layoutStory);
  const layoutKind = storyKind(layoutStory);
  const findQueue = (
    predicate: (storyId: string) => boolean,
  ) => queues.find((candidate) => predicate(candidate.storyId)) ?? null;

  const exact = findQueue((storyId) => normalizeStoryKey(storyId) === layoutKey);
  if (exact) return exact;
  if (isBodyStory(layoutStory)) return findQueue((storyId) => isBodyStory(storyId));
  if (layoutRid) {
    const byRid = findQueue((storyId) => relationshipId(storyId) === layoutRid);
    if (byRid) return byRid;
  }
  if (layoutKind !== 'header' && layoutKind !== 'footer') return null;

  const layoutIndex = uniqueStoriesOfKind(fragments.map((fragment) => fragment.story), layoutKind)
    .findIndex((story) => storyIdentityKey(story) === storyIdentityKey(layoutStory));
  if (layoutIndex < 0) return null;
  const engineStoryId = uniqueStoriesOfKind(queues.map((queue) => queue.storyId), layoutKind)[layoutIndex];
  if (!engineStoryId) return null;
  return findQueue((storyId) => (
    storyId === engineStoryId
    || storyIdentityKey(storyId) === storyIdentityKey(engineStoryId)
  ));
}

function takeMatchingTable(
  layoutStory: string,
  queues: Array<{ storyId: string; tables: SuperDocListedTable[] }>,
  fragments: SuperDocTableFragmentRef[],
): { storyId: string; table: SuperDocListedTable } | null {
  const queue = matchingQueueForLayoutStory(layoutStory, fragments, queues);
  if (!queue) return null;
  const table = queue.tables.shift();
  if (!table) return null;
  return { storyId: queue.storyId, table };
}

export function readSuperDocTableFragments(root: ParentNode | null | undefined): SuperDocTableFragmentRef[] {
  if (!root || typeof root.querySelectorAll !== 'function') return [];

  const seen = new Set<string>();
  const fragments: SuperDocTableFragmentRef[] = [];
  for (const node of root.querySelectorAll('.superdoc-table-fragment')) {
    const blockId = node.getAttribute('data-layout-block-ref')
      || node.getAttribute('data-block-id');
    if (!blockId) continue;
    const seenKey = `${node.getAttribute('data-layout-story') || 'body'}::${layoutTableKey(blockId)}`;
    if (seen.has(seenKey)) continue;
    seen.add(seenKey);
    fragments.push({
      blockId,
      story: node.getAttribute('data-layout-story') || 'body',
    });
  }
  return fragments;
}

function collectTableFragments(
  roots: Array<ParentNode | null | undefined>,
  fallback?: HTMLElement,
): SuperDocTableFragmentRef[] {
  const seen = new Set<string>();
  const fragments: SuperDocTableFragmentRef[] = [];
  for (const root of roots) {
    for (const fragment of readSuperDocTableFragments(root)) {
      const seenKey = `${fragment.story}::${layoutTableKey(fragment.blockId)}`;
      if (seen.has(seenKey)) continue;
      seen.add(seenKey);
      fragments.push(fragment);
    }
  }
  if (fallback instanceof HTMLElement) {
    const blockId = fragmentBlockId(fallback);
    if (blockId) {
      const seenKey = `${fallback.getAttribute('data-layout-story') || 'body'}::${layoutTableKey(blockId)}`;
      if (!seen.has(seenKey)) {
        fragments.push({
          blockId,
          story: fallback.getAttribute('data-layout-story') || 'body',
        });
      }
    }
  }
  return fragments;
}

function rootsAround(fragment: Element, stage: ParentNode | null | undefined): ParentNode[] {
  const roots: ParentNode[] = [];
  const add = (node: ParentNode | null | undefined) => {
    if (node && !roots.includes(node)) roots.push(node);
  };
  const root = fragment.getRootNode();
  if (root instanceof Document || root instanceof ShadowRoot) add(root);
  add(stage);
  add(fragment.ownerDocument);
  return roots;
}

function tableFragmentFromEvent(event: PointerEvent, target: Element): HTMLElement | null {
  const nested = target.closest('.superdoc-table-fragment');
  if (nested instanceof HTMLElement) return nested;
  for (const node of document.elementsFromPoint(event.clientX, event.clientY)) {
    if (!(node instanceof Element)) continue;
    if (node.closest('[data-sd-table-resize-overlay], .v2-table-resize-overlay')) continue;
    const fragment = node.closest('.superdoc-table-fragment');
    if (fragment instanceof HTMLElement) return fragment;
  }
  return null;
}

/**
 * Returns a `tables.list()` payload whose flattened tableNodeId order matches
 * unique table fragments in the layout DOM. Leftover tables (not currently
 * mounted) stay at the end so the list stays complete.
 */
export function alignListedTablesWithFragments(
  listed: SuperDocTableListResult | null | undefined,
  fragments: SuperDocTableFragmentRef[],
): SuperDocTableListResult | null | undefined {
  if (!listed || fragments.length === 0) return listed;

  const queues = storyQueues(listed);
  if (queues.every((queue) => queue.tables.length === 0)) return listed;

  const aligned: SuperDocListedTableStory[] = [];
  for (const fragment of fragments) {
    const match = takeMatchingTable(fragment.story, queues, fragments);
    if (!match) continue;
    aligned.push({ storyId: match.storyId, tables: [match.table] });
  }

  for (const queue of queues) {
    if (queue.tables.length === 0) continue;
    aligned.push({ storyId: queue.storyId, tables: queue.tables });
  }

  if (aligned.length === 0) return listed;
  return { ...listed, stories: aligned };
}

export function resolveTableNodeIdForBlock(
  listed: SuperDocTableListResult | null | undefined,
  fragments: SuperDocTableFragmentRef[],
  blockId: string,
): string | null {
  if (!listed || !blockId) return null;
  const targetStory = fragments.find((fragment) => blockIdsMatch(fragment.blockId, blockId))?.story
    ?? (storyKind(blockId) === 'other' ? 'body' : blockId);

  const queue = matchingQueueForLayoutStory(targetStory, fragments, storyQueues(listed));
  if (!queue) return null;
  const tables = queue.tables;

  const related: SuperDocTableFragmentRef[] = [];
  const seen = new Set<string>();
  for (const fragment of fragments) {
    if (!storiesAlign(targetStory, fragment.story)) continue;
    const key = layoutTableKey(fragment.blockId);
    if (seen.has(key)) continue;
    seen.add(key);
    related.push(fragment);
  }
  related.sort((left, right) => (layoutTableOrdinal(left.blockId) ?? 0) - (layoutTableOrdinal(right.blockId) ?? 0));

  // Index among *mounted* fragments is only safe when the mounted set is
  // complete for this story. A virtualized window can omit earlier tables, so
  // subset index 0 would write the first engine table instead of the grabbed
  // one. Refuse rather than corrupt a live document.
  if (related.length !== tables.length) return null;

  const index = related.findIndex((fragment) => blockIdsMatch(fragment.blockId, blockId));
  if (index < 0) return null;
  return tables[index]?.tableNodeId ?? null;
}

export interface SuperDocTableColumnBoundary {
  i: number;
  x: number;
  w: number;
  min?: number;
}

export function parseTableColumnBoundaries(raw: string | null): SuperDocTableColumnBoundary[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as { columns?: Array<Record<string, unknown>> };
    return (parsed.columns ?? [])
      .map((column) => ({
        i: Number(column.i ?? column.index),
        x: Number(column.x),
        w: Number(column.w),
        min: column.min == null ? undefined : Number(column.min),
      }))
      .filter((column) => Number.isFinite(column.i) && Number.isFinite(column.x) && Number.isFinite(column.w))
      .sort((left, right) => left.i - right.i);
  } catch {
    return [];
  }
}

export interface SuperDocTableRowBoundary {
  i: number;
  y: number;
  h: number;
  min?: number;
}

export function parseTableRowBoundaries(raw: string | null): SuperDocTableRowBoundary[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as { rows?: Array<Record<string, unknown>> };
    return (parsed.rows ?? [])
      .map((row) => ({
        i: Number(row.i ?? row.index),
        y: Number(row.y),
        h: Number(row.h ?? row.height),
        min: row.min == null ? undefined : Number(row.min),
      }))
      .filter((row) => Number.isFinite(row.i) && Number.isFinite(row.y) && Number.isFinite(row.h))
      .sort((left, right) => left.i - right.i);
  } catch {
    return [];
  }
}

export function hitTestTableColumnEdge(
  columns: SuperDocTableColumnBoundary[],
  fragmentWidth: number,
  localX: number,
  slopPx = 6,
): { leftIndex: number; rightIndex: number | null } | null {
  if (columns.length === 0 || fragmentWidth <= 0) return null;
  const total = columns.reduce((sum, column) => sum + column.w, 0);
  if (total <= 0) return null;
  const scale = fragmentWidth / total;

  for (let index = 0; index < columns.length; index += 1) {
    const column = columns[index];
    const edgeX = (column.x + column.w) * scale;
    if (Math.abs(localX - edgeX) > slopPx) continue;
    const next = columns[index + 1];
    return {
      leftIndex: column.i,
      rightIndex: next ? next.i : null,
    };
  }
  return null;
}

export function columnHitFromResizeHandle(
  handle: Element,
  columns: SuperDocTableColumnBoundary[],
): { leftIndex: number; rightIndex: number | null } | null {
  const axis = handle.getAttribute('data-sd-resize-axis');
  if (axis && axis !== 'column') return null;
  const rawIndex = handle.getAttribute('data-sd-resize-index');
  if (rawIndex == null || columns.length === 0) return null;
  const boundaryIndex = Number(rawIndex);
  if (!Number.isFinite(boundaryIndex) || boundaryIndex <= 0) return null;
  const last = columns[columns.length - 1];
  if (boundaryIndex >= columns.length) {
    return { leftIndex: last.i, rightIndex: null };
  }
  const right = columns.find((column) => column.i === boundaryIndex) ?? columns[boundaryIndex];
  const left = columns.find((column) => column.i === boundaryIndex - 1) ?? columns[boundaryIndex - 1];
  if (!left) return null;
  return { leftIndex: left.i, rightIndex: right ? right.i : null };
}

export function hitTestTableCell(
  columns: SuperDocTableColumnBoundary[],
  rows: SuperDocTableRowBoundary[],
  fragmentWidth: number,
  fragmentHeight: number,
  localX: number,
  localY: number,
): { rowIndex: number; columnIndex: number } | null {
  if (columns.length === 0 || rows.length === 0 || fragmentWidth <= 0 || fragmentHeight <= 0) {
    return null;
  }
  const totalWidth = columns.reduce((sum, column) => sum + column.w, 0);
  const totalHeight = rows.reduce((sum, row) => sum + row.h, 0);
  if (totalWidth <= 0 || totalHeight <= 0) return null;

  const scaleX = fragmentWidth / totalWidth;
  const scaleY = fragmentHeight / totalHeight;
  const column = columns.find((candidate) => {
    const left = candidate.x * scaleX;
    return localX >= left && localX < left + candidate.w * scaleX;
  }) ?? (localX >= fragmentWidth ? columns[columns.length - 1] : null);
  const row = rows.find((candidate) => {
    const top = candidate.y * scaleY;
    return localY >= top && localY < top + candidate.h * scaleY;
  }) ?? (localY >= fragmentHeight ? rows[rows.length - 1] : null);
  if (!column || !row) return null;
  return { rowIndex: row.i, columnIndex: column.i };
}

export function hitTestTableRowEdge(
  rows: SuperDocTableRowBoundary[],
  fragmentHeight: number,
  localY: number,
  slopPx = 6,
): { rowIndex: number } | null {
  if (rows.length === 0 || fragmentHeight <= 0) return null;
  const total = rows.reduce((sum, row) => sum + row.h, 0);
  if (total <= 0) return null;
  const scale = fragmentHeight / total;

  for (const row of rows) {
    const edgeY = (row.y + row.h) * scale;
    if (Math.abs(localY - edgeY) > slopPx) continue;
    return { rowIndex: row.i };
  }
  return null;
}

export function rowHitFromResizeHandle(
  handle: Element,
  rows: SuperDocTableRowBoundary[],
): { rowIndex: number } | null {
  const axis = handle.getAttribute('data-sd-resize-axis');
  if (axis && axis !== 'row') return null;
  const rawIndex = handle.getAttribute('data-sd-resize-index');
  if (rawIndex == null || rows.length === 0) return null;
  const rowIndex = Number(rawIndex);
  if (!Number.isFinite(rowIndex) || rowIndex < 0) return null;
  const row = rows.find((candidate) => candidate.i === rowIndex) ?? rows[rowIndex];
  return row ? { rowIndex: row.i } : null;
}

const MIN_COLUMN_WIDTH_PX = 24;
const MIN_ROW_HEIGHT_PX = 24;
const RESIZE_GUIDELINE_CLASS = 'crimson-table-resize-guideline';

export function screenToLayoutDelta(deltaPx: number, scale: number): number {
  if (!Number.isFinite(deltaPx)) return 0;
  if (!Number.isFinite(scale) || scale <= 0) return deltaPx;
  return deltaPx / scale;
}

function layoutScale(layoutSize: number, screenSize: number): number {
  if (!Number.isFinite(layoutSize) || !Number.isFinite(screenSize) || layoutSize <= 0 || screenSize <= 0) {
    return 1;
  }
  return screenSize / layoutSize;
}

export function appliedColumnResizeDelta(
  widths: number[],
  leftIndex: number,
  rightIndex: number | null,
  deltaPx: number,
  minWidth = MIN_COLUMN_WIDTH_PX,
): number {
  let applied = deltaPx;
  if (leftIndex >= 0 && leftIndex < widths.length) {
    applied = Math.max(minWidth - widths[leftIndex], applied);
    if (rightIndex != null && rightIndex < widths.length) {
      applied = Math.min(widths[rightIndex] - minWidth, applied);
    }
  }
  return applied;
}

export function appliedRowResizeDelta(
  height: number,
  deltaPx: number,
  minHeight = MIN_ROW_HEIGHT_PX,
): number {
  return Math.max(minHeight, height + deltaPx) - height;
}

export function columnWidthsAfterScreenResize(
  widths: number[],
  leftIndex: number,
  rightIndex: number | null,
  screenDeltaPx: number,
  scale: number,
): number[] {
  const next = [...widths];
  const applied = appliedColumnResizeDelta(
    next,
    leftIndex,
    rightIndex,
    screenToLayoutDelta(screenDeltaPx, scale),
  );
  if (leftIndex >= 0 && leftIndex < next.length) {
    next[leftIndex] += applied;
    if (rightIndex != null && rightIndex < next.length) next[rightIndex] -= applied;
  }
  return next;
}

export function rowHeightAfterScreenResize(
  height: number,
  screenDeltaPx: number,
  scale: number,
): number {
  return height + appliedRowResizeDelta(height, screenToLayoutDelta(screenDeltaPx, scale));
}

export function rowResizePreviewLocalY(
  rows: SuperDocTableRowBoundary[],
  rowIndex: number,
  fragmentHeight: number,
  deltaPx: number,
): number {
  const total = rows.reduce((sum, row) => sum + row.h, 0);
  if (total <= 0 || fragmentHeight <= 0) return 0;
  const scale = fragmentHeight / total;
  const row = rows.find((candidate) => candidate.i === rowIndex) ?? rows[rowIndex];
  if (!row) return 0;
  return (row.y + row.h) * scale + appliedRowResizeDelta(row.h, screenToLayoutDelta(deltaPx, scale)) * scale;
}

export function columnResizePreviewLocalX(
  widths: number[],
  leftIndex: number,
  fragmentWidth: number,
  deltaPx: number,
  rightIndex: number | null = null,
): number {
  const total = widths.reduce((sum, width) => sum + width, 0);
  if (total <= 0 || fragmentWidth <= 0 || leftIndex < 0) return 0;
  const scale = fragmentWidth / total;
  const edge = widths.slice(0, leftIndex + 1).reduce((sum, width) => sum + width, 0);
  return edge * scale + appliedColumnResizeDelta(
    widths,
    leftIndex,
    rightIndex,
    screenToLayoutDelta(deltaPx, scale),
  ) * scale;
}

function resizeGuidelineElement(): HTMLElement {
  const existing = document.querySelector(`.${RESIZE_GUIDELINE_CLASS}`);
  if (existing instanceof HTMLElement) return existing;
  const guideline = document.createElement('div');
  guideline.className = RESIZE_GUIDELINE_CLASS;
  guideline.setAttribute('aria-hidden', 'true');
  guideline.style.position = 'fixed';
  guideline.style.zIndex = '2147483646';
  guideline.style.width = '2px';
  guideline.style.marginLeft = '-1px';
  guideline.style.background = '#2563eb';
  guideline.style.pointerEvents = 'none';
  document.body.appendChild(guideline);
  return guideline;
}

function showResizeGuideline(
  fragment: HTMLElement,
  axis: 'column' | 'row',
  local: number,
) {
  const rect = fragment.getBoundingClientRect();
  const guideline = resizeGuidelineElement();
  if (axis === 'row') {
    guideline.style.left = `${rect.left}px`;
    guideline.style.top = `${rect.top + local}px`;
    guideline.style.width = `${rect.width}px`;
    guideline.style.height = '2px';
    guideline.style.marginLeft = '0';
    guideline.style.marginTop = '-1px';
    return;
  }
  guideline.style.left = `${rect.left + local}px`;
  guideline.style.top = `${rect.top}px`;
  guideline.style.width = '2px';
  guideline.style.height = `${rect.height}px`;
  guideline.style.marginLeft = '-1px';
  guideline.style.marginTop = '0';
}

function hideResizeGuideline() {
  for (const node of document.querySelectorAll(`.${RESIZE_GUIDELINE_CLASS}`)) {
    node.remove();
  }
  if (document.body.style.cursor === 'col-resize' || document.body.style.cursor === 'row-resize') {
    document.body.style.cursor = '';
  }
}

function fragmentBlockId(fragment: Element): string | null {
  return fragment.getAttribute('data-layout-block-ref') || fragment.getAttribute('data-block-id');
}

function isThenable(value: unknown): value is Promise<unknown> {
  return Boolean(value && typeof value === 'object' && typeof (value as Promise<unknown>).then === 'function');
}

export function isRejectedSuperDocReceipt(receipt: unknown): boolean {
  if (!receipt || typeof receipt !== 'object') return false;
  const record = receipt as { ok?: unknown; success?: unknown };
  return record.success === false || record.ok === false;
}

async function callTables(tables: Record<string, unknown>, name: string, input: unknown): Promise<unknown> {
  const invoked = await invokeTables(tables, name, input);
  return invoked.receipt;
}

async function invokeTables(
  tables: Record<string, unknown>,
  name: string,
  input: unknown,
): Promise<{ ok: boolean; receipt: unknown }> {
  const method = tables[name];
  if (typeof method !== 'function') return { ok: false, receipt: null };
  try {
    const result = method.call(tables, input);
    const receipt = isThenable(result) ? await result : result;
    if (isRejectedSuperDocReceipt(receipt)) return { ok: false, receipt };
    return { ok: true, receipt };
  } catch {
    return { ok: false, receipt: null };
  }
}

export async function writeTableColumnWidths(
  tables: Record<string, unknown>,
  nodeId: string,
  widthsPx: number[],
): Promise<boolean> {
  for (let columnIndex = 0; columnIndex < widthsPx.length; columnIndex += 1) {
    const widthPt = cssPxToPt(widthsPx[columnIndex]);
    const invoked = await invokeTables(tables, 'setColumnWidth', {
      nodeId,
      columnIndex,
      widthPt,
    });
    if (!invoked.ok) return false;
  }
  const layout = await invokeTables(tables, 'setLayout', {
    nodeId,
    autoFitMode: 'fixedWidth',
    preferredWidth: Math.round(widthsPx.reduce((sum, width) => sum + cssPxToPt(width), 0) * 20),
    preferredWidthType: 'dxa',
  });
  return layout.ok;
}

export async function writeTableRowHeight(
  tables: Record<string, unknown>,
  nodeId: string,
  rowIndex: number,
  heightPx: number,
): Promise<boolean> {
  const heightPt = cssPxToPt(heightPx);
  const invoked = await invokeTables(tables, 'setRowHeight', {
    nodeId,
    rowIndex,
    heightPt,
    rule: 'exact',
  });
  return invoked.ok;
}

function cssPxToPt(px: number): number {
  return px * 0.75;
}

export interface SuperDocDocumentApi {
  blocks?: Record<string, unknown>;
  query?: Record<string, unknown>;
  tables: Record<string, unknown>;
}

interface TableResizeHost {
  getSource: () => unknown;
  getStage: () => ParentNode | null | undefined;
  onMutated?: () => void;
}

const hosts: TableResizeHost[] = [];
let drag: {
  fragment: HTMLElement;
  host: TableResizeHost;
  scale: number;
  startX: number;
  startY: number;
} & (
  | {
    axis: 'column';
    leftIndex: number;
    rightIndex: number | null;
    widths: number[];
  }
  | {
    axis: 'row';
    rowIndex: number;
    height: number;
    rows: SuperDocTableRowBoundary[];
  }
) | null = null;
let listenersBound = false;
let commitInFlight = false;

function bindPointerDownListener() {
  if (listenersBound || typeof document === 'undefined') return;
  document.addEventListener('pointerdown', onPointerDown, true);
  listenersBound = true;
}

function unbindPointerDownListener() {
  if (!listenersBound || typeof document === 'undefined') return;
  document.removeEventListener('pointerdown', onPointerDown, true);
  listenersBound = false;
}

function clearActiveDrag() {
  drag = null;
  hideResizeGuideline();
  if (typeof window === 'undefined') return;
  window.removeEventListener('pointermove', onPointerMove, true);
  window.removeEventListener('pointerup', onPointerUp, true);
  window.removeEventListener('pointercancel', onPointerUp, true);
}

function uninstallHost(host: TableResizeHost) {
  const index = hosts.indexOf(host);
  if (index >= 0) hosts.splice(index, 1);
  if (drag?.host === host) clearActiveDrag();
  if (hosts.length === 0) unbindPointerDownListener();
}

function hostForFragment(fragment: Element): TableResizeHost | null {
  return hosts.find((host) => {
    const stage = host.getStage();
    return Boolean(stage && stage instanceof Node && stage.contains(fragment));
  }) ?? null;
}

export function countInstalledTableResizeHosts(): number {
  return hosts.length;
}

export function stageForTableResizeFragment(fragment: Element): ParentNode | null {
  const stage = hostForFragment(fragment)?.getStage();
  return stage && stage instanceof Node ? stage : null;
}

export function resetTableResizeTargetForTests() {
  hosts.length = 0;
  commitInFlight = false;
  clearActiveDrag();
  unbindPointerDownListener();
}

function widthsFromColumns(columns: SuperDocTableColumnBoundary[]): number[] {
  const maxIndex = columns.reduce((max, column) => Math.max(max, column.i), -1);
  const widths = Array.from({ length: maxIndex + 1 }, () => 0);
  for (const column of columns) {
    widths[column.i] = column.w;
  }
  return widths;
}

function readProperty(value: unknown, key: string): unknown {
  if (!value || (typeof value !== 'object' && typeof value !== 'function')) return undefined;
  try {
    return (value as Record<string, unknown>)[key];
  } catch {
    return undefined;
  }
}

function isNamespace(value: unknown): value is object {
  return Boolean(value) && (typeof value === 'object' || typeof value === 'function');
}

export function isSuperDocTablesMutationApi(value: unknown): value is Record<string, unknown> {
  return isNamespace(value) && typeof readProperty(value, 'setColumnWidth') === 'function';
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return isNamespace(value) ? value as Record<string, unknown> : undefined;
}

export function readSuperDocDocumentApi(source: unknown): SuperDocDocumentApi | null {
  const seen = new Set<unknown>();
  const queue: unknown[] = [source];

  while (queue.length > 0) {
    const current = queue.shift();
    if (!isNamespace(current) || seen.has(current)) continue;
    seen.add(current);

    const tables = readProperty(current, 'tables');
    if (isSuperDocTablesMutationApi(tables)) {
      return {
        tables,
        blocks: asRecord(readProperty(current, 'blocks')),
        query: asRecord(readProperty(current, 'query')),
      };
    }

    queue.push(
      readProperty(current, 'doc'),
      readProperty(current, 'activeEditor'),
      readProperty(current, 'documentApi'),
    );
  }

  return null;
}

export function readSuperDocTablesApi(source: unknown): Record<string, unknown> | null {
  return readSuperDocDocumentApi(source)?.tables ?? null;
}

function storyLocatorFromLayoutStory(storyId: string): Record<string, unknown> {
  const refId = storyId.match(/rId\d+/i)?.[0];
  if (refId && !isBodyStory(storyId)) {
    return { kind: 'story', storyType: 'headerFooterPart', refId };
  }
  return { kind: 'story', storyType: 'body' };
}

function nodeIdsFromUnknown(value: unknown): string[] {
  if (!value || typeof value !== 'object') return [];
  const record = value as {
    blocks?: Array<{ nodeId?: unknown; nodeType?: unknown }>;
    items?: Array<{ handle?: { ref?: unknown }; address?: { nodeId?: unknown }; nodeId?: unknown }>;
    matches?: Array<{ nodeId?: unknown }>;
    stories?: SuperDocListedTableStory[];
  };
  if (Array.isArray(record.blocks)) {
    return record.blocks
      .filter((block) => !block.nodeType || block.nodeType === 'table')
      .map((block) => (typeof block.nodeId === 'string' ? block.nodeId : ''))
      .filter(Boolean);
  }
  if (Array.isArray(record.items)) {
    return record.items
      .map((item) => {
        if (typeof item.nodeId === 'string') return item.nodeId;
        if (typeof item.address?.nodeId === 'string') return item.address.nodeId;
        if (typeof item.handle?.ref === 'string') return item.handle.ref;
        return '';
      })
      .filter(Boolean);
  }
  if (Array.isArray(record.matches)) {
    return record.matches
      .map((match) => (typeof match.nodeId === 'string' ? match.nodeId : ''))
      .filter(Boolean);
  }
  return flattenListedTableNodeIds(record);
}

async function listTablesInStory(
  documentApi: SuperDocDocumentApi,
  storyId: string,
): Promise<SuperDocListedTable[]> {
  const locator = storyLocatorFromLayoutStory(storyId);
  if (documentApi.blocks && typeof documentApi.blocks.list === 'function') {
    const listed = await callTables(documentApi.blocks, 'list', {
      nodeTypes: ['table'],
      in: locator,
    });
    const ids = nodeIdsFromUnknown(listed);
    if (ids.length > 0) return ids.map((tableNodeId) => ({ tableNodeId }));
  }
  if (documentApi.query && typeof documentApi.query.match === 'function') {
    const matched = await callTables(documentApi.query, 'match', {
      select: { type: 'node', nodeType: 'table' },
      in: locator,
    });
    return nodeIdsFromUnknown(matched).map((tableNodeId) => ({ tableNodeId }));
  }
  return [];
}

export async function listTablesForResize(
  documentApi: SuperDocDocumentApi,
  fragments: SuperDocTableFragmentRef[],
): Promise<SuperDocTableListResult | null> {
  const listed = await callTables(documentApi.tables, 'list', undefined);
  if (listed && typeof listed === 'object' && Array.isArray((listed as SuperDocTableListResult).stories)) {
    return listed as SuperDocTableListResult;
  }

  const stories: SuperDocListedTableStory[] = [];
  const seen = new Set<string>();
  const storyIds = fragments.length > 0 ? fragments.map((fragment) => fragment.story) : ['body'];
  for (const storyId of storyIds) {
    const key = normalizeStoryKey(storyId) || 'body';
    if (seen.has(key)) continue;
    seen.add(key);
    stories.push({
      storyId,
      tables: await listTablesInStory(documentApi, storyId),
    });
  }
  return { stories };
}

function documentApiForHost(host: TableResizeHost): SuperDocDocumentApi | null {
  return readSuperDocDocumentApi(host.getSource());
}

async function resolveDragTableNodeId(currentDrag: NonNullable<typeof drag>): Promise<{
  documentApi: SuperDocDocumentApi;
  nodeId: string;
} | null> {
  const documentApi = documentApiForHost(currentDrag.host);
  if (!documentApi) return null;
  const blockId = fragmentBlockId(currentDrag.fragment) ?? '';
  const fragments = collectTableFragments(
    rootsAround(currentDrag.fragment, currentDrag.host.getStage()),
    currentDrag.fragment,
  );
  const listed = await listTablesForResize(documentApi, fragments);
  const nodeId = resolveTableNodeIdForBlock(listed, fragments, blockId);
  if (!nodeId) return null;
  return { documentApi, nodeId };
}

async function commitColumnResize(
  currentDrag: Extract<NonNullable<typeof drag>, { axis: 'column' }>,
  deltaPx: number,
): Promise<void> {
  const next = columnWidthsAfterScreenResize(
    currentDrag.widths,
    currentDrag.leftIndex,
    currentDrag.rightIndex,
    deltaPx,
    currentDrag.scale,
  );
  if (next.every((width, index) => Math.abs(width - currentDrag.widths[index]) < 0.5)) {
    return;
  }

  const resolved = await resolveDragTableNodeId(currentDrag);
  if (!resolved) return;
  const { documentApi, nodeId } = resolved;
  const wrote = await writeTableColumnWidths(documentApi.tables, nodeId, next);
  if (!wrote) {
    return;
  }
  currentDrag.host.onMutated?.();
}

async function commitRowResize(
  currentDrag: Extract<NonNullable<typeof drag>, { axis: 'row' }>,
  deltaPx: number,
): Promise<void> {
  const nextHeight = rowHeightAfterScreenResize(currentDrag.height, deltaPx, currentDrag.scale);
  if (Math.abs(nextHeight - currentDrag.height) < 0.5) {
    return;
  }

  const resolved = await resolveDragTableNodeId(currentDrag);
  if (!resolved) return;
  const { documentApi, nodeId } = resolved;
  const wrote = await writeTableRowHeight(
    documentApi.tables,
    nodeId,
    currentDrag.rowIndex,
    nextHeight,
  );
  if (!wrote) {
    return;
  }
  currentDrag.host.onMutated?.();
}

function onPointerMove(event: PointerEvent) {
  if (!drag) return;
  event.preventDefault();
  const rect = drag.fragment.getBoundingClientRect();
  if (drag.axis === 'row') {
    showResizeGuideline(
      drag.fragment,
      'row',
      rowResizePreviewLocalY(drag.rows, drag.rowIndex, rect.height, event.clientY - drag.startY),
    );
    return;
  }
  showResizeGuideline(
    drag.fragment,
    'column',
    columnResizePreviewLocalX(
      drag.widths,
      drag.leftIndex,
      rect.width,
      event.clientX - drag.startX,
      drag.rightIndex,
    ),
  );
}

function onPointerUp(event: PointerEvent) {
  const currentDrag = drag;
  clearActiveDrag();
  if (!currentDrag) return;
  const deltaPx = currentDrag.axis === 'row'
    ? event.clientY - currentDrag.startY
    : event.clientX - currentDrag.startX;
  commitInFlight = true;
  const commit = currentDrag.axis === 'row'
    ? commitRowResize(currentDrag, deltaPx)
    : commitColumnResize(currentDrag, deltaPx);
  void commit.finally(() => {
    commitInFlight = false;
  });
}

function onPointerDown(event: PointerEvent) {
  if (event.button !== 0 || drag || commitInFlight) return;
  const target = event.target;
  if (!(target instanceof Element)) return;

  const resizeHandle = target.closest('.v2-table-resize-handle');
  const overlayTarget = target.closest('[data-sd-table-resize-overlay], .v2-table-resize-overlay');
  const fragment = tableFragmentFromEvent(event, target);
  if (!fragment && !resizeHandle && !overlayTarget) return;

  // Steal SuperDoc's own handle drag so it cannot resize the wrong table.
  // Leave overlay/cell clicks alone — swallowing those drops caret, selection,
  // and the undo stack.
  if (resizeHandle) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  if (!(fragment instanceof HTMLElement) || !fragment.closest('.draft-docx-editor--editing')) return;

  const host = hostForFragment(fragment);
  if (!host) return;

  const documentApi = documentApiForHost(host);
  if (!documentApi) return;

  const boundaries = fragment.getAttribute('data-table-boundaries');
  const columns = parseTableColumnBoundaries(boundaries);
  const rows = parseTableRowBoundaries(boundaries);
  const rect = fragment.getBoundingClientRect();
  const localX = event.clientX - rect.left;
  const localY = event.clientY - rect.top;
  const handleAxis = resizeHandle instanceof Element
    ? resizeHandle.getAttribute('data-sd-resize-axis')
    : null;
  const rowHit = handleAxis === 'column'
    ? null
    : (
      (resizeHandle instanceof Element ? rowHitFromResizeHandle(resizeHandle, rows) : null)
      ?? (handleAxis === 'row' ? null : hitTestTableRowEdge(rows, rect.height, localY))
    );
  const columnHit = handleAxis === 'row'
    ? null
    : (
      (resizeHandle instanceof Element ? columnHitFromResizeHandle(resizeHandle, columns) : null)
      ?? hitTestTableColumnEdge(columns, rect.width, localX)
    );

  const startRowDrag = Boolean(rowHit && (handleAxis === 'row' || !columnHit));
  if (startRowDrag && rowHit) {
    const row = rows.find((candidate) => candidate.i === rowHit.rowIndex) ?? rows[rowHit.rowIndex];
    if (!row) return;
    const total = rows.reduce((sum, candidate) => sum + candidate.h, 0);
    const scale = layoutScale(total, rect.height);
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    drag = {
      axis: 'row',
      fragment,
      host,
      rowIndex: rowHit.rowIndex,
      height: row.h,
      rows,
      scale,
      startX: event.clientX,
      startY: event.clientY,
    };
    document.body.style.cursor = 'row-resize';
    showResizeGuideline(
      fragment,
      'row',
      rowResizePreviewLocalY(rows, drag.rowIndex, rect.height, 0),
    );
    window.addEventListener('pointermove', onPointerMove, true);
    window.addEventListener('pointerup', onPointerUp, true);
    window.addEventListener('pointercancel', onPointerUp, true);
    return;
  }

  if (columns.length === 0 || !columnHit) return;

  const widths = widthsFromColumns(columns);
  const totalWidth = widths.reduce((sum, width) => sum + width, 0);
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
  drag = {
    axis: 'column',
    fragment,
    host,
    leftIndex: columnHit.leftIndex,
    rightIndex: columnHit.rightIndex,
    scale: layoutScale(totalWidth, rect.width),
    startX: event.clientX,
    startY: event.clientY,
    widths,
  };
  document.body.style.cursor = 'col-resize';
  showResizeGuideline(
    fragment,
    'column',
    columnResizePreviewLocalX(drag.widths, drag.leftIndex, rect.width, 0, drag.rightIndex),
  );
  window.addEventListener('pointermove', onPointerMove, true);
  window.addEventListener('pointerup', onPointerUp, true);
  window.addEventListener('pointercancel', onPointerUp, true);
}

export function installSuperDocTableResizeTargetFix(options: {
  getSource: () => unknown;
  getStage: () => ParentNode | null | undefined;
  onMutated?: () => void;
}): () => void {
  const host: TableResizeHost = {
    getSource: options.getSource,
    getStage: options.getStage,
    onMutated: options.onMutated,
  };
  hosts.push(host);
  bindPointerDownListener();

  return () => uninstallHost(host);
}
